// Perfis da Visão Geral (tela inicial, rota `/`) — #6493, projeto 24.
//
// Cada pessoa abre o sistema numa tela feita para o trabalho dela. O perfil sai
// do papel e do departamento do cadastro; `profiles.tela_inicial` cobre as
// exceções (ex.: Igor e Vinicius têm papel adm e departamento Financeiro, como a
// Meyryene, mas veem a tela da Diretoria). Decisões em
// docs/superpowers/specs/2026-10-27-visao-geral-por-perfil-design.md.
//
// Lógica pura: roda sob `node --test` com type-stripping.

import type { Profile } from '../../../types';
import { temDepartamento } from '../departamentos.ts';

export const PERFIS_VISAO_GERAL = [
  'diretoria',
  'comercial',
  'vendas',
  'parceiros',
  'gestao-financeiro',
  'faturamento',
  'backoffice',
  'cobranca',
  'pos-venda',
  'tecnologia-marketing',
  'tecnologia',
  'marketing',
  'campo',
  'padrao',
] as const;

export type PerfilVisaoGeral = typeof PERFIS_VISAO_GERAL[number];

export interface DefinicaoPerfil {
  id: PerfilVisaoGeral;
  rotulo: string;
  /** A pergunta que a tela responde, mostrada abaixo da saudação. */
  pergunta: string;
  /**
   * 'dia': tela operacional, abre no dia e sem seletor de período.
   * 'mes': gestão, abre no mês atual com seletor.
   */
  periodo: 'dia' | 'mes';
}

export const DEFINICOES_PERFIL: Record<PerfilVisaoGeral, DefinicaoPerfil> = {
  'diretoria': { id: 'diretoria', rotulo: 'Diretoria', pergunta: 'A empresa está crescendo com o caixa saudável?', periodo: 'mes' },
  'comercial': { id: 'comercial', rotulo: 'Gestão Comercial', pergunta: 'Vamos bater a meta de energia contratada, e onde o funil está travando?', periodo: 'mes' },
  'vendas': { id: 'vendas', rotulo: 'Vendas', pergunta: 'Quem eu atendo agora, e quanto falta para a minha meta?', periodo: 'mes' },
  'parceiros': { id: 'parceiros', rotulo: 'Parceiros', pergunta: 'Como estão os clientes que eu trouxe, e quanto vou receber?', periodo: 'mes' },
  'gestao-financeiro': { id: 'gestao-financeiro', rotulo: 'Gestão do Financeiro', pergunta: 'O dinheiro do mês está entrando, e o faturamento e a cobrança estão em dia?', periodo: 'mes' },
  'faturamento': { id: 'faturamento', rotulo: 'Faturamento', pergunta: 'O que eu preciso calcular e emitir hoje, e estou dentro do prazo?', periodo: 'dia' },
  'backoffice': { id: 'backoffice', rotulo: 'Backoffice do Faturamento', pergunta: 'O rateio do mês está certo, e as faturas e as usinas estão em dia?', periodo: 'dia' },
  'cobranca': { id: 'cobranca', rotulo: 'Cobrança', pergunta: 'Quem eu preciso contatar hoje, e quanto já recuperei?', periodo: 'dia' },
  'pos-venda': { id: 'pos-venda', rotulo: 'Pós-venda', pergunta: 'Quais análises e clientes novos precisam de mim hoje?', periodo: 'dia' },
  'tecnologia-marketing': { id: 'tecnologia-marketing', rotulo: 'Tecnologia e Marketing', pergunta: 'O sistema está de pé, as demandas estão no prazo, e a mídia está trazendo contrato?', periodo: 'mes' },
  'tecnologia': { id: 'tecnologia', rotulo: 'Tecnologia', pergunta: 'O sistema está de pé, e as demandas estão no prazo?', periodo: 'mes' },
  'marketing': { id: 'marketing', rotulo: 'Marketing', pergunta: 'A mídia está trazendo contato barato que vira contrato?', periodo: 'mes' },
  'campo': { id: 'campo', rotulo: 'Campo', pergunta: 'Qual é a minha rota de hoje?', periodo: 'dia' },
  'padrao': { id: 'padrao', rotulo: 'Tela padrão', pergunta: 'O que eu tenho para hoje?', periodo: 'dia' },
};

export const ehPerfilVisaoGeral = (v: unknown): v is PerfilVisaoGeral =>
  typeof v === 'string' && (PERFIS_VISAO_GERAL as readonly string[]).includes(v);

/** O que a regra lê do cadastro. `tela_inicial` só existe depois da migration 20261027210000. */
export type PessoaPerfil = Pick<Profile, 'role' | 'departamento' | 'departamentos'> & {
  tela_inicial?: string | null;
};

const PAPEIS_SEM_TELA_PROPRIA = new Set(['empresa_parceira_admin', 'empresa_parceira_operador', 'geradoras_externo', 'consumidor']);

/** O perfil que o papel e o departamento indicam, sem olhar `tela_inicial`. */
export function perfilAutomatico(p: PessoaPerfil): PerfilVisaoGeral {
  const role = String(p.role || '');
  if (role === 'motoboy') return 'campo';
  if (role === 'parceiro' || role === 'vendedor_parceiro') return 'parceiros';
  // Gerência comercial é um cargo só (decisão de 27/10/2026, #6510): gerente de parcerias também.
  if (role === 'gerente' || role === 'gerente_parcerias') return 'comercial';
  if (PAPEIS_SEM_TELA_PROPRIA.has(role)) return 'padrao';

  const tem = (d: string) => temDepartamento(p, d);
  if (tem('Tecnologia') && tem('Marketing')) return 'tecnologia-marketing';
  if (tem('Tecnologia')) return 'tecnologia';
  if (tem('Marketing')) return 'marketing';
  if (tem('Faturamento')) return 'faturamento';
  if (tem('Pós Venda')) return 'pos-venda';
  // No Financeiro, quem é adm é a gestão; o resto da equipe é a Cobrança.
  if (tem('Financeiro')) return role === 'adm' ? 'gestao-financeiro' : 'cobranca';
  // tem('Comercial') também acha os nomes antigos (Comercial Interno/Externo).
  if (tem('Comercial') || tem('Parcerias')) return 'vendas';
  if (role === 'adm') return 'diretoria';
  return 'padrao';
}

/** O perfil da tela inicial: `tela_inicial` quando preenchida e válida; senão, o automático. */
export function resolverPerfil(p: PessoaPerfil): PerfilVisaoGeral {
  return ehPerfilVisaoGeral(p.tela_inicial) ? p.tela_inicial : perfilAutomatico(p);
}
